// src/service/OrdenCompra.js
import instance from "../apis/app";

const OrdenCompra = {
  /**
   * Crea orden desde carrito
   * POST /api/ordenes/crear
   * @param {Object} datosCliente - Datos del cliente
   * @param {boolean} requiereFactura - ¿Requiere factura?
   * @param {string} tipoDocumentoSolicitado - Tipo de documento
   * @returns {Promise}
   */
  Crear: async (datosCliente, requiereFactura = false, tipoDocumentoSolicitado = 'boleta') => {
    try {
      const response = await instance.post('/ordenes/crear', {
        datosCliente,
        requiereFactura,
        tipoDocumentoSolicitado
      });
      return response.data.data || response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Obtiene orden por ID
   * GET /api/ordenes/:id
   * @param {string} ordenId - ID de la orden
   * @returns {Promise}
   */
  Get: async (ordenId) => {
    try {
      const response = await instance.get(`/ordenes/${ordenId}`);
      return response.data.data || response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Obtiene órdenes del usuario
   * GET /api/ordenes/mis-ordenes
   * @param {string} estado - Estado opcional (creada, completada, etc)
   * @returns {Promise}
   */
  MisOrdenes: async (estado = null) => {
    try {
      const url = estado ? `/ordenes/mis-ordenes?estado=${estado}` : '/ordenes/mis-ordenes';
      const response = await instance.get(url);
      return response.data.data || [];
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Cancela orden
   * POST /api/ordenes/:id/cancelar
   * @param {string} ordenId - ID de la orden
   * @param {string} motivo - Motivo de cancelación
   * @returns {Promise}
   */
  Cancelar: async (ordenId, motivo = 'Cancelado por usuario') => {
    try {
      const response = await instance.post(`/ordenes/${ordenId}/cancelar`, { motivo });
      return response.data.data || response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Lista todas las órdenes completadas con filtros
   * GET /api/ordenes/completadas
   * @param {Object} filtros - Objeto con filtros opcionales
   * @param {string} filtros.recintoId - ID del recinto
   * @param {string} filtros.fechaInicio - Fecha inicio formato YYYY-MM-DD
   * @param {string} filtros.fechaFin - Fecha fin formato YYYY-MM-DD
   * @param {number} filtros.page - Número de página (default: 1)
   * @param {number} filtros.limit - Items por página (default: 20)
   * @param {string} filtros.usuarioId - ID del usuario (opcional)
   * @returns {Promise<Object>} { ordenes, total, page, totalPages, porPagina }
   */
  ListarCompletadas: async (filtros = {}) => {
    try {
      const params = new URLSearchParams();
      
      if (filtros.recintoId) params.append('recintoId', filtros.recintoId);
      if (filtros.fechaInicio) params.append('fechaInicio', filtros.fechaInicio);
      if (filtros.fechaFin) params.append('fechaFin', filtros.fechaFin);
      if (filtros.page) params.append('page', filtros.page);
      if (filtros.limit) params.append('limit', filtros.limit);
      if (filtros.usuarioId) params.append('usuarioId', filtros.usuarioId);
      if (filtros.nombre) params.append('nombre', filtros.nombre);
      if (filtros.rut) params.append('rut', filtros.rut);
      if (filtros.email) params.append('email', filtros.email);

      const url = `/ordenes/completadas${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await instance.get(url);

      console.log('Respuesta de ListarCompletadas:', response);
      
      return response.data || {
        ordenes: [],
        total: 0,
        page: 1,
        totalPages: 0,
        porPagina: 20
      };
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Obtiene detalle completo de una orden con información de boletas
   * GET /api/ordenes/:id/detalle
   * @param {string} ordenId - ID de la orden
   * @returns {Promise<Object>} Orden completa con documentosBoleta y urlBoleta
   */
  GetDetalle: async (ordenId) => {
    try {
      const response = await instance.get(`/ordenes/${ordenId}/detalle`);
      return response.data || response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Obtiene estadísticas de órdenes completadas
   * GET /api/ordenes/estadisticas
   * @param {Object} filtros - Filtros opcionales
   * @param {string} filtros.fechaInicio - Fecha inicio formato YYYY-MM-DD
   * @param {string} filtros.fechaFin - Fecha fin formato YYYY-MM-DD
   * @param {string} filtros.recintoId - ID del recinto
   * @returns {Promise<Object>} Estadísticas agregadas
   */
  GetEstadisticas: async (filtros = {}) => {
    try {
      const params = new URLSearchParams();
      
      if (filtros.fechaInicio) params.append('fechaInicio', filtros.fechaInicio);
      if (filtros.fechaFin) params.append('fechaFin', filtros.fechaFin);
      if (filtros.recintoId) params.append('recintoId', filtros.recintoId);

      const url = `/ordenes/estadisticas${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await instance.get(url);
      
      return response.data || {
        totalOrdenes: 0,
        totalRecaudado: 0,
        totalDescuentos: 0,
        promedioOrden: 0,
        ordenMaxima: 0,
        ordenMinima: 0,
        totalItemsPagados: 0,
        totalItemsGratuitos: 0
      };
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Abre la boleta en una nueva ventana
   * @param {string} ordenId - ID de la orden
   * @returns {Promise<void>}
   */
  AbrirBoleta: async (ordenId) => {
    try {
      const detalle = await OrdenCompra.GetDetalle(ordenId);
      
      if (detalle.urlBoleta) {
        window.open(detalle.urlBoleta, '_blank');
      } else if (detalle.documentosBoleta && detalle.documentosBoleta.length > 0) {
        // Si hay múltiples boletas, abrir la primera
        window.open(detalle.documentosBoleta[0].urlPublica, '_blank');
      } else {
        throw new Error('No hay boleta disponible para esta orden');
      }
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Descarga la boleta en formato PDF
   * @param {string} ordenId - ID de la orden
   * @param {number} indiceDocumento - Índice del documento si hay múltiples (default: 0)
   * @returns {Promise<void>}
   */
  DescargarBoleta: async (ordenId, indiceDocumento = 0) => {
    try {
      const detalle = await OrdenCompra.GetDetalle(ordenId);
      
      let urlPdf = null;
      
      if (detalle.documentosBoleta && detalle.documentosBoleta[indiceDocumento]) {
        urlPdf = detalle.documentosBoleta[indiceDocumento].urlPdf;
      }
      
      if (urlPdf) {
        window.open(urlPdf, '_blank');
      } else {
        throw new Error('No hay PDF disponible para esta boleta');
      }
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

export default OrdenCompra;
